import { jwtDecode } from 'jwt-decode';

/**
 * Client-side decode of the PISP JWT for display purposes only.
 *
 * The forms app receives the payment JWT as a flow input (`init.input.jwt`).
 * The new platform contract reads the payment data (amount, concept,
 * customer ids, …) from this JWT's claims server-side, so the `/banks`
 * response no longer carries a `params` block. We decode the same JWT here
 * to render the payment summary and the merchant logo.
 *
 * This is NOT verification — `jwt-decode` never checks the signature, and
 * neither do we. The backend verifies the JWT before acting on it. We only
 * read the (already trusted) payload to paint the screen, so a tampered
 * token at worst shows wrong display values to the payer who minted it; it
 * can never authorise a payment from the client.
 */

/** PISP claims we surface, mirroring `app.json.jwtClaims[]`. All optional. */
export type PispClaims = {
  customerId?: string;
  externalCustomerId?: string;
  externalClientId?: string;
  amount?: string;
  currency?: string;
  concept?: string;
  callbackUrl?: string;
  customField1?: string;
  customField2?: string;
  customField3?: string;
  customField4?: string;
  customField5?: string;
};

const CLAIM_KEYS: (keyof PispClaims)[] = [
  'customerId',
  'externalCustomerId',
  'externalClientId',
  'amount',
  'currency',
  'concept',
  'callbackUrl',
  'customField1',
  'customField2',
  'customField3',
  'customField4',
  'customField5',
];

/**
 * Decode the PISP claims from a JWT. Returns `null` for anything that isn't
 * a parseable JWT payload — callers fall back to a degraded summary rather
 * than crashing. Known claims are coerced to strings (e.g. a numeric
 * `amount`); unknown claims are dropped.
 */
export const decodePispJwt = (jwt: string): PispClaims | null => {
  try {
    const raw = jwtDecode<Record<string, unknown>>(jwt);
    const claims: PispClaims = {};
    for (const key of CLAIM_KEYS) {
      const value = raw[key];
      if (value !== undefined && value !== null) {
        claims[key] = String(value);
      }
    }
    return claims;
  } catch {
    return null;
  }
};
